import { useEffect, useRef } from 'react';
import { useQuery } from '@maat/store-hooks';
import { useToast } from '@maat/ds-core';
import { useTranslation } from '@maat/store-i18n';
import { bustedReload, setUpdatePending } from '@maat/store-error';
import { isEntryChunkSkewed } from '@maat/utils';
import { buildInfo } from './buildInfo';
import { getExecutingEntryUrl } from './executingEntry';

// Shape of the /version.json manifest emitted by the deploy workflow.
interface VersionManifest {
    sha: string;
    version: string;
    buildNumber: number;
    builtAt: string;
    /** Hashed chunk basenames the deployed entry bootstrap imports; see
     * src/executingEntry.ts. Absent on manifests older than that check. */
    entryChunks?: string[];
}

const VERSION_URL = '/version.json';
const POLL_INTERVAL_MS = 60_000;

async function fetchManifest(): Promise<VersionManifest> {
    // no-store so we always hit the (short-TTL) origin copy, not a stale bfcache one.
    const res = await fetch(VERSION_URL, { cache: 'no-store' });
    if (!res.ok) throw new Error(`version.json ${res.status}`);
    return res.json();
}

/**
 * Polls /version.json and offers a Reload toast once the live SHA differs from the
 * one this tab booted with. Inert without a known SHA (local dev). SHA, not semver,
 * so it fires per deploy. Mounted once in the shell — it covers every remote.
 */
export const VersionWatcher = () => {
    const { toast } = useToast();
    const { t } = useTranslation('common');
    const notified = useRef(false);

    const { data } = useQuery<VersionManifest>({
        queryKey: ['app-version-manifest'],
        queryFn: fetchManifest,
        enabled: Boolean(buildInfo.sha),
        refetchInterval: POLL_INTERVAL_MS,
        // refetchOnWindowFocus stays at its default (on) → free check on tab focus.
        staleTime: 0,
        retry: false,
    });

    useEffect(() => {
        const liveSha = data?.sha;
        if (!liveSha || !buildInfo.sha) return;
        if (liveSha === buildInfo.sha) {
            // A matching SHA only proves the DOCUMENT is current. Running code
            // loaded from a chunk the deployed bootstrap does not import means a
            // stale cached subresource — treat it as a pending update.
            if (!isEntryChunkSkewed(data?.entryChunks, getExecutingEntryUrl())) return;
        }

        // Latch FIRST (idempotent): the ErrorBoundary and UpdateMigrator both act on
        // this whether or not the toast is still showing.
        setUpdatePending(liveSha);

        if (notified.current) return;
        notified.current = true;
        toast({
            title: t('appUpdate.newVersionTitle'),
            description: t('appUpdate.newVersionDescription'),
            variant: 'default',
            duration: Infinity,
            action: {
                label: t('appUpdate.reload'),
                // Unguarded (the 20s guard is for involuntary crash recovery) but
                // cache-busted: a plain reload re-serves the immutable-cached entry
                // and the toast returns forever.
                onClick: () => bustedReload(),
            },
        });
    }, [data?.sha, data?.entryChunks, toast, t]);

    return null;
};
